import React, { useState, useCallback, useMemo } from 'react';
import { Plus, Download, Trash2, Edit2, X, Save, DollarSign, TrendingDown, Calendar as CalendarIcon, Search, Filter, FileText } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { REVENUE_TYPES, EXPENSE_CATEGORIES, PAYMENT_METHODS } from '../../utils/constants';
import { formatCurrency, formatDate, generateReference, hasPermission, filterTransactionsByPermission, canViewZavatsoa, exportToCSV } from '../../utils/helpers';
import { validateTransactionForm, confirmLargeAmount } from '../../utils/formValidation';
import Modal from '../common/Modal';
import Button from '../common/Button';
import ErrorMessage from '../common/ErrorMessage';

const TransactionsView = () => {
  const { data, addDonation, updateDonation, deleteDonation, addExpense, updateExpense, deleteExpense } = useData();
  const { currentUser } = useAuth();
  
  // États pour le formulaire
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [transactionType, setTransactionType] = useState('R'); // R ou D
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, R, D
  const [filterMonth, setFilterMonth] = useState('all');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [formErrors, setFormErrors] = useState({}); // Erreurs de validation
  const [formError, setFormError] = useState(''); // Erreur générale
  
  const [form, setForm] = useState({
    libelle: '',
    amount: '',
    date: new Date().toISOString().split('T')[0], // Date du jour par défaut
    type: '',
    method: '',
    category: '',
    recipient: '',
    description: '',
    notes: '',
    status: 'completed'
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Fusionner donations et expenses en une seule liste (mémoïsée)
  const allTransactionsRaw = useMemo(() => {
    return [
      ...(data.donations || []).map(d => ({
        ...d,
        transactionType: 'R',
        originalId: d.id,
        displayId: d.reference
      })),
      ...(data.expenses || []).map(e => ({
        ...e,
        transactionType: 'D',
        originalId: e.id,
        displayId: e.reference
      }))
    ].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [data.donations, data.expenses]);

  // Filtrer selon les permissions (cacher Zavatsoa aux membres) - mémoïsé
  const allTransactions = useMemo(
    () => filterTransactionsByPermission(allTransactionsRaw, currentUser),
    [allTransactionsRaw, currentUser]
  );

  // Filtrer les transactions - Optimisé avec useMemo
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter(t => {
      const matchesSearch =
        t.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || t.transactionType === filterType;
      
      const matchesMonth =
        filterMonth === 'all' ||
        (t.date && t.date.startsWith(filterMonth));
      
      return matchesSearch && matchesType && matchesMonth;
    });
  }, [allTransactions, searchTerm, filterType, filterMonth]);

  // Pagination: calcul des éléments de la page courante
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / itemsPerPage));

  // Totaux (réels + visibles) mémoïsés
  const {
    totalRevenus,
    totalDepenses,
    solde,
    totalRevenusVisibles,
    totalDepensesVisibles,
    soldeVisible,
  } = useMemo(() => {
    // 🔒 Totaux RÉELS (avec TOUTES les transactions, y compris Zavatsoa)
    const totalRevenus = (data.donations || []).reduce(
      (sum, d) => sum + (parseFloat(d.amount) || 0),
      0
    );
    const totalDepenses = (data.expenses || []).reduce(
      (sum, e) => sum + (parseFloat(e.amount) || 0),
      0
    );
    const solde = totalRevenus - totalDepenses;

    // 👀 Totaux visibles (sans Zavatsoa pour certains utilisateurs)
    const visibleDonations = filterTransactionsByPermission(data.donations || [], currentUser);
    const visibleExpenses = filterTransactionsByPermission(data.expenses || [], currentUser);
    const totalRevenusVisibles = visibleDonations.reduce(
      (sum, d) => sum + (parseFloat(d.amount) || 0),
      0
    );
    const totalDepensesVisibles = visibleExpenses.reduce(
      (sum, e) => sum + (parseFloat(e.amount) || 0),
      0
    );
    const soldeVisible = totalRevenusVisibles - totalDepensesVisibles;

    return {
      totalRevenus,
      totalDepenses,
      solde,
      totalRevenusVisibles,
      totalDepensesVisibles,
      soldeVisible,
    };
  }, [data.donations, data.expenses, currentUser]);
  
  // Vérifier si l'utilisateur peut voir Zavatsoa
  const showingAllTransactions = canViewZavatsoa(currentUser);

  // Réinitialiser le formulaire
  const resetForm = () => {
    setForm({
      libelle: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      type: '',
      method: '',
      category: '',
      recipient: '',
      description: '',
      notes: '',
      status: 'completed'
    });
    setFormErrors({});
    setFormError('');
    setShowForm(false);
    setEditingId(null);
    setTransactionType('R');
  };

  // Gérer l'ajout/modification - Avec validation améliorée
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Réinitialiser les erreurs
    setFormErrors({});
    setFormError('');
    
    // Validation du formulaire
    const validation = validateTransactionForm(form);
    if (!validation.valid) {
      setFormErrors(validation.errors);
      setFormError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    // Confirmation pour les montants importants
    const amount = parseFloat(form.amount);
    if (!confirmLargeAmount(amount, transactionType)) {
      return;
    }

    try {
      if (editingId) {
        // Modification - editingId est déjà l'originalId (Firebase id)
        const transaction = allTransactions.find(t => (t.originalId || t.id) === editingId);
        if (transaction.transactionType === 'R') {
          updateDonation(editingId, form);
        } else {
          updateExpense(editingId, form);
        }
      } else {
        // Ajout
        // Compter les transactions pour la même date
        const transactionsOfSameDate = allTransactions.filter(t => 
          t.date === form.date && 
          t.transactionType === transactionType
        );
        
        const newTransaction = {
          reference: generateReference(transactionType, form.date, transactionsOfSameDate.length),
          libelle: form.libelle,
          amount: amount,
          date: form.date,
          method: form.method,
          notes: form.notes,
          status: form.status
        };

        if (transactionType === 'R') {
          newTransaction.type = form.type;
          addDonation(newTransaction);
        } else {
          newTransaction.category = form.category;
          newTransaction.recipient = form.recipient;
          newTransaction.description = form.description;
          newTransaction.paymentMethod = form.method;
          addExpense(newTransaction);
        }
      }
      
      resetForm();
    } catch (error) {
      setFormError('Une erreur est survenue lors de l\'enregistrement. Veuillez réessayer.');
      logger.error('Erreur handleSubmit:', error);
    }
  };

  // Charger une transaction pour modification - Optimisé avec useCallback
  const handleEdit = useCallback((transaction) => {
    const canEdit = hasPermission(currentUser, 'edit_finances') || 
                    hasPermission(currentUser, 'edit_all') || 
                    currentUser?.role === 'admin';
    
    if (!canEdit) {
      alert('Vous n\'avez pas la permission de modifier les transactions');
      return;
    }

    setEditingId(transaction.originalId || transaction.id);
    setTransactionType(transaction.transactionType);
    setForm({
      libelle: transaction.libelle || '',
      amount: transaction.amount || '',
      date: transaction.date || '',
      type: transaction.type || '',
      method: transaction.method || transaction.paymentMethod || '',
      category: transaction.category || '',
      recipient: transaction.recipient || '',
      description: transaction.description || '',
      notes: transaction.notes || '',
      status: transaction.status || 'completed'
    });
    setShowForm(true);
  }, [currentUser]);


  // Supprimer une transaction - Optimisé avec useCallback
  const handleDelete = useCallback((transaction) => {
    const canDelete = hasPermission(currentUser, 'delete_finances') || 
                      hasPermission(currentUser, 'edit_all') || 
                      currentUser?.role === 'admin';
    
    if (!canDelete) {
      alert('Vous n\'avez pas la permission de supprimer les transactions');
      return;
    }

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la transaction ${transaction.reference} ?\n\nMontant: ${formatCurrency(transaction.amount)}\nLibellé: ${transaction.libelle}`)) {
      if (transaction.transactionType === 'R') {
        deleteDonation(transaction.originalId || transaction.id);
      } else {
        deleteExpense(transaction.originalId || transaction.id);
      }
    }
  }, [currentUser, deleteDonation, deleteExpense]);

  // Afficher le reçu ou justificatif
  const handleShowReceipt = (transaction) => {
    setSelectedTransaction(transaction);
    setShowReceiptModal(true);
  };

  // Imprimer le reçu ou justificatif
  const handlePrintReceipt = () => {
    const printWindow = window.open('', '', 'width=800,height=600');
    
    const isRevenue = selectedTransaction.transactionType === 'R';
    const docTitle = isRevenue ? 'REÇU DE REVENU' : 'JUSTIFICATIF DE DÉPENSE';
    const docColor = isRevenue ? '#22c55e' : '#ef4444';
    const docBgColor = isRevenue ? '#f0fdf4' : '#fef2f2';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${isRevenue ? 'Reçu' : 'Justificatif'} - ${selectedTransaction.reference}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              color: #333;
            }
            .header p {
              margin: 5px 0;
              font-size: 14px;
              color: #666;
            }
            .title {
              font-size: 18px;
              font-weight: bold;
              color: ${docColor};
              margin-top: 15px;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 12px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .info-label {
              font-weight: 600;
              color: #666;
            }
            .info-value {
              color: #333;
              text-align: right;
              max-width: 60%;
            }
            .amount-box {
              background-color: ${docBgColor};
              padding: 20px;
              border-radius: 8px;
              margin: 30px 0;
              text-align: center;
              border: 2px solid ${docColor};
            }
            .amount-label {
              font-size: 16px;
              color: #666;
              margin-bottom: 10px;
            }
            .amount-value {
              font-size: 32px;
              font-weight: bold;
              color: ${docColor};
            }
            .footer {
              border-top: 2px solid #333;
              padding-top: 20px;
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            .badge {
              display: inline-block;
              padding: 5px 15px;
              background-color: ${docColor};
              color: white;
              border-radius: 20px;
              font-size: 12px;
              font-weight: bold;
              margin-top: 10px;
            }
            @media print {
              body {
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>VOMM FJKM Hannover</h1>
            <p>Vondron'Olona Mpiara Mivavaka</p>
            <p>Fiangonan'i Jesoa Kristy eto Madagasikara</p>
            <p class="title">${docTitle}</p>
            <span class="badge">${selectedTransaction.reference}</span>
          </div>
          
          <div class="info-row">
            <span class="info-label">Référence:</span>
            <span class="info-value">${selectedTransaction.reference}</span>
          </div>
          
          <div class="info-row">
            <span class="info-label">Date:</span>
            <span class="info-value">${formatDate(selectedTransaction.date)}</span>
          </div>
          
          ${isRevenue ? `
          <div class="info-row">
            <span class="info-label">Libellé:</span>
            <span class="info-value">${selectedTransaction.libelle}</span>
          </div>
          
          ${selectedTransaction.type ? `
          <div class="info-row">
            <span class="info-label">Type:</span>
            <span class="info-value">${selectedTransaction.type}</span>
          </div>
          ` : ''}
          ` : `
          <div class="info-row">
            <span class="info-label">Destinataire:</span>
            <span class="info-value">${selectedTransaction.recipient || '-'}</span>
          </div>
          
          <div class="info-row">
            <span class="info-label">Objet:</span>
            <span class="info-value">${selectedTransaction.libelle}</span>
          </div>
          
          ${selectedTransaction.category ? `
          <div class="info-row">
            <span class="info-label">Catégorie:</span>
            <span class="info-value">${selectedTransaction.category}</span>
          </div>
          ` : ''}
          
          ${selectedTransaction.description ? `
          <div class="info-row">
            <span class="info-label">Description:</span>
            <span class="info-value">${selectedTransaction.description}</span>
          </div>
          ` : ''}
          `}
          
          ${selectedTransaction.method || selectedTransaction.paymentMethod ? `
          <div class="info-row">
            <span class="info-label">Méthode de paiement:</span>
            <span class="info-value">${selectedTransaction.method || selectedTransaction.paymentMethod}</span>
          </div>
          ` : ''}
          
          ${selectedTransaction.notes ? `
          <div class="info-row">
            <span class="info-label">Notes:</span>
            <span class="info-value">${selectedTransaction.notes}</span>
          </div>
          ` : ''}
          
          <div class="amount-box">
            <div class="amount-label">${isRevenue ? 'Montant Reçu' : 'Montant Payé'}</div>
            <div class="amount-value">${formatCurrency(selectedTransaction.amount)}</div>
          </div>
          
          <div class="footer">
            <p>${isRevenue 
              ? 'Ce reçu confirme la réception du montant mentionné ci-dessus.' 
              : 'Ce justificatif confirme le paiement du montant mentionné ci-dessus par VOMM FJKM Hannover.'
            }</p>
            <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
            ${!isRevenue ? '<p style="margin-top: 15px; font-weight: bold;">Pour usage interne uniquement</p>' : ''}
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  // Export Excel
  const handleExport = () => {
    const exportData = filteredTransactions.map(t => ({
      Référence: t.reference,
      Type: t.transactionType === 'R' ? 'Revenu' : 'Dépense',
      Date: formatDate(t.date),
      Libellé: t.libelle,
      Montant: t.amount,
      Méthode: t.method || t.paymentMethod || '-',
      Catégorie: t.type || t.category || '-',
      Statut: t.status || '-'
    }));
    
    exportToCSV(exportData, 'transactions');
  };

  // Obtenir les mois disponibles pour le filtre
  const availableMonths = [...new Set(allTransactions.map(t => t.date?.substring(0, 7)))].sort().reverse();

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenus</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenus)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Dépenses</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDepenses)}</p>
            </div>
            <TrendingDown className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 ${solde >= 0 ? 'border-blue-500' : 'border-orange-500'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Solde</p>
              <p className={`text-2xl font-bold ${solde >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                {formatCurrency(solde)}
              </p>
            </div>
            <DollarSign className={`w-8 h-8 ${solde >= 0 ? 'text-blue-500' : 'text-orange-500'}`} />
          </div>
        </div>
      </div>

      {/* Message informatif pour les membres */}
      {!canViewZavatsoa(currentUser) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                Informations sur les transactions affichées
              </h4>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                Par mesure de confidentialité, certaines transactions sensibles (catégorie "Zavatsoa") ne sont pas affichées dans la liste ci-dessous, mais elles sont bien incluses dans le calcul du solde réel. Seuls les responsables financiers (Président, Pasteur, Trésorier, Administrateur) peuvent voir le détail de toutes les transactions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Barre d'actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            {/* Recherche */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                id="search-transactions"
                name="search"
                placeholder="Rechercher une transaction..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Filtre par type */}
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tous les types</option>
              <option value="R">Revenus uniquement</option>
              <option value="D">Dépenses uniquement</option>
            </select>

            {/* Filtre par mois */}
            <select
              value={filterMonth}
              onChange={(e) => {
                setFilterMonth(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tous les mois</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exporter
            </button>

            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouvelle Transaction
            </button>
          </div>
        </div>
      </div>

      {/* Formulaire d'ajout/modification */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold dark:text-white">
              {editingId ? 'Modifier la Transaction' : 'Nouvelle Transaction'}
            </h3>
            <button
              onClick={resetForm}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Message d'erreur général */}
            {formError && (
              <ErrorMessage 
                message={formError} 
                type="error"
                onClose={() => setFormError('')}
              />
            )}
            
            {/* Boutons toggle pour choisir le type de transaction */}
            {!editingId && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type de transaction *
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setTransactionType('R')}
                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-lg border-2 transition-all duration-200 ${
                      transactionType === 'R'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 shadow-md'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-green-400 dark:hover:border-green-500'
                    }`}
                  >
                    <DollarSign className={`w-6 h-6 ${transactionType === 'R' ? 'text-green-600 dark:text-green-400' : ''}`} />
                    <div className="text-left">
                      <div className="font-semibold">Revenu</div>
                      <div className="text-xs opacity-75">Entrée d'argent</div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setTransactionType('D')}
                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-lg border-2 transition-all duration-200 ${
                      transactionType === 'D'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 shadow-md'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-400 dark:hover:border-red-500'
                    }`}
                  >
                    <TrendingDown className={`w-6 h-6 ${transactionType === 'D' ? 'text-red-600 dark:text-red-400' : ''}`} />
                    <div className="text-left">
                      <div className="font-semibold">Dépense</div>
                      <div className="text-xs opacity-75">Sortie d'argent</div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Type de transaction en lecture seule lors de la modification */}
            {editingId && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-gray-300 dark:border-gray-600">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type de transaction
                </label>
                <div className="flex items-center gap-2">
                  {transactionType === 'R' ? (
                    <>
                      <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <span className="font-semibold text-green-700 dark:text-green-400">Revenu (Entrée d'argent)</span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                      <span className="font-semibold text-red-700 dark:text-red-400">Dépense (Sortie d'argent)</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Le type de transaction ne peut pas être modifié après création
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Date */}
              <div>
                <label htmlFor="transaction-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  id="transaction-date"
                  name="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                    formErrors.date ? 'border-red-500 dark:border-red-400' : 'dark:border-gray-600'
                  }`}
                  required
                />
                {formErrors.date && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1">{formErrors.date}</p>
                )}
              </div>

              {/* Libellé */}
              <div className="md:col-span-2">
                <label htmlFor="transaction-libelle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Libellé *
                </label>
                <input
                  type="text"
                  id="transaction-libelle"
                  name="libelle"
                  value={form.libelle}
                  onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                  placeholder="Description de la transaction"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                    formErrors.libelle ? 'border-red-500 dark:border-red-400' : 'dark:border-gray-600'
                  }`}
                  required
                />
                {formErrors.libelle && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1">{formErrors.libelle}</p>
                )}
              </div>

              {/* Montant */}
              <div>
                <label htmlFor="transaction-amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Montant (€) *
                </label>
                <input
                  type="number"
                  id="transaction-amount"
                  name="amount"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                    formErrors.amount ? 'border-red-500 dark:border-red-400' : 'dark:border-gray-600'
                  }`}
                  required
                />
                {formErrors.amount && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1">{formErrors.amount}</p>
                )}
              </div>

              {/* Type/Catégorie selon le type de transaction */}
              {transactionType === 'R' ? (
                <div>
                  <label htmlFor="transaction-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type de revenu
                  </label>
                  <select
                    id="transaction-type"
                    name="type"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Sélectionner...</option>
                    {REVENUE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label htmlFor="transaction-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Catégorie
                  </label>
                  <select
                    id="transaction-category"
                    name="category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Sélectionner...</option>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Méthode de paiement */}
              <div>
                <label htmlFor="transaction-method" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Méthode de paiement
                </label>
                <select
                  id="transaction-method"
                  name="method"
                  value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value })}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Sélectionner...</option>
                  {PAYMENT_METHODS.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>

              {/* Destinataire (uniquement pour les dépenses) */}
              {transactionType === 'D' && (
                <div>
                  <label htmlFor="transaction-recipient" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Destinataire
                  </label>
                  <input
                    type="text"
                    id="transaction-recipient"
                    name="recipient"
                    value={form.recipient}
                    onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}

              {/* Notes/Description */}
              <div className="md:col-span-2">
                <label htmlFor="transaction-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {transactionType === 'D' ? 'Description' : 'Notes'}
                </label>
                <textarea
                  id="transaction-notes"
                  name={transactionType === 'D' ? 'description' : 'notes'}
                  value={transactionType === 'D' ? form.description : form.notes}
                  onChange={(e) => setForm({ 
                    ...form, 
                    [transactionType === 'D' ? 'description' : 'notes']: e.target.value 
                  })}
                  rows="3"
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Boutons */}
            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des transactions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Référence
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Libellé
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedTransactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    Aucune transaction trouvée
                  </td>
                </tr>
              ) : (
                paginatedTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                        transaction.transactionType === 'R'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {transaction.transactionType === 'R' ? (
                          <><DollarSign className="w-3 h-3" /> Revenu</>
                        ) : (
                          <><TrendingDown className="w-3 h-3" /> Dépense</>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                        {transaction.reference}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(transaction.date, 'short')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {transaction.libelle}
                      </div>
                      {transaction.recipient && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          • {transaction.recipient}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {transaction.type || transaction.category || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-semibold ${
                        transaction.transactionType === 'R'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {transaction.transactionType === 'R' ? '+' : '-'}
                        {formatCurrency(transaction.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleShowReceipt(transaction)}
                          className={`p-1 hover:bg-opacity-10 rounded transition-colors ${
                            transaction.transactionType === 'R'
                              ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                              : 'text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                          }`}
                          title={transaction.transactionType === 'R' ? 'Voir le reçu' : 'Voir le justificatif'}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        {(hasPermission(currentUser, 'edit_finances') || hasPermission(currentUser, 'edit_all') || currentUser?.role === 'admin') && (
                          <button
                            onClick={() => handleEdit(transaction)}
                            className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                        {(hasPermission(currentUser, 'delete_finances') || hasPermission(currentUser, 'edit_all') || currentUser?.role === 'admin') && (
                          <button
                            onClick={() => handleDelete(transaction)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination + compteur de résultats */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400 flex-col md:flex-row gap-2">
        <div className="text-center md:text-left">
          {filteredTransactions.length > 0 ? (
            <span>
              Affichage{' '}
              <span className="font-medium">
                {(currentPage - 1) * itemsPerPage + 1}
              </span>
              {' '}à{' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, filteredTransactions.length)}
              </span>
              {' '}sur{' '}
              <span className="font-medium">
                {filteredTransactions.length}
              </span>
              {' '}transaction{filteredTransactions.length > 1 ? 's' : ''}
              {(searchTerm || filterType !== 'all' || filterMonth !== 'all') && (
                <>
                  {' '} (sur {allTransactions.length} au total)
                </>
              )}
            </span>
          ) : (
            <span>Aucune transaction trouvée</span>
          )}
        </div>

        <div className="flex items-center gap-2 justify-center md:justify-end">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md border text-xs md:text-sm ${
              currentPage === 1
                ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600'
            }`}
          >
            Précédent
          </button>
          <span>
            Page <span className="font-medium">{currentPage}</span> / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || filteredTransactions.length === 0}
            className={`px-3 py-1 rounded-md border text-xs md:text-sm ${
              currentPage === totalPages || filteredTransactions.length === 0
                ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600'
            }`}
          >
            Suivant
          </button>
        </div>
      </div>

      {/* Modal de Reçu/Justificatif */}
      {showReceiptModal && selectedTransaction && (
        <Modal
          isOpen={showReceiptModal}
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedTransaction(null);
          }}
          title={selectedTransaction.transactionType === 'R' ? 'Reçu de Revenu' : 'Justificatif de Dépense'}
          size="md"
        >
          <div className="receipt-content space-y-6">
            {/* En-tête du reçu/justificatif */}
            <div className="text-center border-b-2 border-gray-300 pb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">VOMM FJKM Hannover</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Vondron'Olona Mpiara Mivavaka</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Fiangonan'i Jesoa Kristy eto Madagasikara</p>
              <p className={`text-lg font-semibold mt-3 ${
                selectedTransaction.transactionType === 'R' 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {selectedTransaction.transactionType === 'R' ? 'REÇU DE REVENU' : 'JUSTIFICATIF DE DÉPENSE'}
              </p>
            </div>

            {/* Informations */}
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Référence:</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">{selectedTransaction.reference}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Date:</span>
                <span className="text-sm text-gray-900 dark:text-white">{formatDate(selectedTransaction.date)}</span>
              </div>

              {selectedTransaction.transactionType === 'R' ? (
                <>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Libellé:</span>
                    <span className="text-sm text-gray-900 dark:text-white">{selectedTransaction.libelle}</span>
                  </div>

                  {selectedTransaction.type && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Type:</span>
                      <span className="text-sm text-gray-900 dark:text-white">{selectedTransaction.type}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {selectedTransaction.recipient && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Destinataire:</span>
                      <span className="text-sm text-gray-900 dark:text-white">{selectedTransaction.recipient}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Objet:</span>
                    <span className="text-sm text-gray-900 dark:text-white">{selectedTransaction.libelle}</span>
                  </div>

                  {selectedTransaction.category && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Catégorie:</span>
                      <span className="text-sm text-gray-900 dark:text-white">{selectedTransaction.category}</span>
                    </div>
                  )}

                  {selectedTransaction.description && (
                    <div className="py-2 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1">Description:</span>
                      <span className="text-sm text-gray-900 dark:text-white">{selectedTransaction.description}</span>
                    </div>
                  )}
                </>
              )}

              {(selectedTransaction.method || selectedTransaction.paymentMethod) && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Méthode de paiement:</span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {selectedTransaction.method || selectedTransaction.paymentMethod}
                  </span>
                </div>
              )}

              {selectedTransaction.notes && (
                <div className="py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1">Notes:</span>
                  <span className="text-sm text-gray-900 dark:text-white">{selectedTransaction.notes}</span>
                </div>
              )}

              {/* Montant en grand */}
              <div className={`rounded-lg p-4 mt-4 ${
                selectedTransaction.transactionType === 'R'
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                    {selectedTransaction.transactionType === 'R' ? 'Montant Reçu:' : 'Montant Payé:'}
                  </span>
                  <span className={`text-3xl font-bold ${
                    selectedTransaction.transactionType === 'R'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(selectedTransaction.amount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Pied de page */}
            <div className="border-t-2 border-gray-300 pt-4 mt-6">
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                {selectedTransaction.transactionType === 'R'
                  ? 'Ce reçu confirme la réception du montant mentionné ci-dessus.'
                  : 'Ce justificatif confirme le paiement du montant mentionné ci-dessus par VOMM FJKM Hannover.'
                }
              </p>
              <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
                Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}
              </p>
              {selectedTransaction.transactionType === 'D' && (
                <p className="text-xs text-center text-gray-600 dark:text-gray-400 mt-2 font-semibold">
                  Pour usage interne uniquement
                </p>
              )}
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-end gap-3 pt-4 border-t print:hidden">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReceiptModal(false);
                  setSelectedTransaction(null);
                }}
              >
                Fermer
              </Button>
              <Button
                onClick={handlePrintReceipt}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Imprimer
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default React.memo(TransactionsView);